import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Typography,
  CircularProgress,
  Box,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import { EmailOutlined } from "@mui/icons-material";
import { Sun, Moon } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import host from "../../global";
import { useTheme } from "../../hooks/useTheme";
import EvaluationReportOrgAdmin from "../shared_components/EvaluationReport";

const SESSION_STORAGE_KEY_PREFIX = "report_session_";

// Positioned relative to the nearest positioned ancestor (each render branch
// below sets one), not the viewport — so it scrolls away with the page instead
// of staying pinned in the corner.
const PublicReportLogo = ({ theme }) => (
  <img
    src={theme === "dark" ? "/genie-demo-logo.png" : "/genie-demo-logo-black.png"}
    alt="GenieHire"
    className="absolute top-4 left-4 sm:top-6 sm:left-6 h-7 sm:h-8 w-auto select-none pointer-events-none"
    style={{ zIndex: 20 }}
  />
);

// Lets a public-link viewer flip the report between dark and light — it defaults
// to dark, but nothing here says the viewer wants that.
const PublicThemeToggle = ({ theme, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
    aria-label="Toggle theme"
    className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center justify-center h-9 w-9 rounded-full border transition-colors"
    style={{
      zIndex: 20,
      borderColor: theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
      backgroundColor: theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#ffffff" : "#333333",
    }}
  >
    {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
  </button>
);

// Public share-token report. This component is only the email + OTP gate; once the
// viewer is verified it renders the exact same <EvaluationReportOrgAdmin> the
// authenticated org/HR report uses, in its public mode (data from a single
// /public-report endpoint, admin-only actions hidden). Keeping one report
// implementation means the two views never drift apart.
const PublicReport = () => {
  const { token } = useParams();

  // Same hook EvaluationReport uses — it already owns syncing <html>'s
  // `.dark`/`.light` class, so both stay consistent with no extra machinery here.
  const { theme: publicTheme, toggleTheme: handleToggleTheme } = useTheme();
  const appTheme = publicTheme;

  // Public links should open dark by default even if the viewer's (or a
  // previewing admin's) saved theme is light — flip once on mount, not on
  // every render.
  const defaultedDark = useRef(false);
  useEffect(() => {
    if (!defaultedDark.current && publicTheme !== "dark") {
      defaultedDark.current = true;
      handleToggleTheme();
    }
  }, [publicTheme, handleToggleTheme]);

  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);
  const [otpError, setOtpError] = useState("");

  const sessionStorageKey = `${SESSION_STORAGE_KEY_PREFIX}${token}`;

  const maskEmail = (email) => {
    if (!email) return "";
    const [username, domain] = email.split("@");
    if (!domain) return email;

    const maskedUsername =
      username.length <= 4
        ? username.charAt(0) +
          "*".repeat(Math.max(username.length - 2, 0)) +
          username.charAt(username.length - 1)
        : username.substring(0, 2) +
          "*".repeat(username.length - 4) +
          username.substring(username.length - 2);

    const [domainName, extension] = domain.split(".");
    const maskedDomain =
      domainName.charAt(0) +
      "*".repeat(Math.max(domainName.length - 2, 0)) +
      domainName.charAt(domainName.length - 1);

    return `${maskedUsername}@${maskedDomain}.${extension}`;
  };

  const handleOtpInput = (e, index, otp, setOtp) => {
    const value = e.target.value;
    if (value.length <= 1) {
      const newOtp = otp.split("");
      newOtp[index] = value.slice(0, 1);
      setOtp(newOtp.join(""));
      if (value && index < 5) {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
    }
  };

  const handleOtpPaste = (e, setOtp) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();
    if (/^\d{6}$/.test(pastedData)) {
      setOtp(pastedData);
      document.getElementById("otp-input-5").focus();
    }
  };

  const handleOtpKeyDown = (e, index, otp, setOtp) => {
    if (e.key === "Backspace") {
      if (index > 0 && !otp[index]) {
        document.getElementById(`otp-input-${index - 1}`).focus();
      } else if (index > 0 && otp[index]) {
        const newOtp = otp.split("");
        newOtp[index] = "";
        setOtp(newOtp.join(""));
      } else if (index === 0) {
        const newOtp = otp.split("");
        newOtp[0] = "";
        setOtp(newOtp.join(""));
      }
    }
  };

  const fetchRecipientEmail = useCallback(async () => {
    try {
      const response = await axios.get(`${host}/get-recipient-email/${token}`);
      setEmail(response.data.email.toLowerCase());
    } catch (error) {
      console.error("Fetch Recipient Email Error:", error);
      toast.error(
        `Error: ${
          error.response?.data?.detail || "Unable to fetch recipient email"
        }`
      );
      setError(
        `Error: ${
          error.response?.data?.detail || "Unable to fetch recipient email"
        }`
      );
    }
  }, [token]);

  const checkSession = useCallback(async () => {
    setEmailLoading(true);
    try {
      const storedSession = sessionStorage.getItem(sessionStorageKey);

      if (!storedSession) {
        await fetchRecipientEmail();
        setEmailLoading(false);
        return;
      }

      let sessionData;
      try {
        sessionData = JSON.parse(storedSession);
      } catch (error) {
        sessionStorage.removeItem(sessionStorageKey);
        await fetchRecipientEmail();
        setEmailLoading(false);
        return;
      }

      const {
        sessionToken: storedToken,
        email: storedEmail,
        expiresAt,
      } = sessionData;

      if (!storedToken || !storedEmail || !expiresAt) {
        sessionStorage.removeItem(sessionStorageKey);
        await fetchRecipientEmail();
        setEmailLoading(false);
        return;
      }

      const expiresAtDate = new Date(expiresAt);
      if (
        isNaN(expiresAtDate.getTime()) ||
        expiresAtDate <= new Date(Date.now() - 60 * 1000)
      ) {
        sessionStorage.removeItem(sessionStorageKey);
        await fetchRecipientEmail();
        setEmailLoading(false);
        return;
      }

      try {
        const response = await axios.post(`${host}/validate-session`, {
          share_token: token,
          email: storedEmail.toLowerCase(),
          session_token: storedToken,
        });

        if (response.status === 200) {
          setEmail(storedEmail.toLowerCase());
          setSessionToken(storedToken);
          setIsVerified(true);
        } else {
          sessionStorage.removeItem(sessionStorageKey);
          await fetchRecipientEmail();
        }
      } catch (error) {
        sessionStorage.removeItem(sessionStorageKey);
        await fetchRecipientEmail();
      }
      setEmailLoading(false);
    } catch (error) {
      console.error("Session check error:", error);
      await fetchRecipientEmail();
      setEmailLoading(false);
    }
  }, [token, sessionStorageKey, fetchRecipientEmail]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const handleGenerateOtp = async () => {
    if (!email) {
      toast.error("Recipient email not available");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${host}/generate-otp`, {
        token,
        email: email.toLowerCase(),
      });
      if (response.status === 200) {
        setOtpSent(true);
        toast.success("OTP sent to your email!");
      }
    } catch (error) {
      console.error("Generate OTP Error:", error);
      toast.error(
        `Error generating OTP: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setVerifyLoading(true);
    setOtpError("");
    try {
      const response = await axios.post(`${host}/verify-otp`, {
        token,
        email: email.toLowerCase(),
        otp,
      });
      if (response.status === 200) {
        const { session_token, session_expires_at } = response.data;

        const sessionData = {
          sessionToken: session_token,
          email: email.toLowerCase(),
          expiresAt: session_expires_at,
        };
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(sessionData));

        setSessionToken(session_token);
        setIsVerified(true);
        toast.success("Verification successful!");
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "An error occured during OTP verification";
      setOtpError(errorMessage);
      toast.error(
        `Error verifying OTP: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setVerifyLoading(false);
    }
  };

  if (emailLoading) {
    return (
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: `${appTheme === "dark" ? "#1f2937" : "white"}`,
        }}
      >
        <PublicReportLogo theme={publicTheme} />
        <PublicThemeToggle theme={publicTheme} onToggle={handleToggleTheme} />
        <CircularProgress sx={{ color: "#4f46e5" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#1f2937",
          color: "white",
        }}
      >
        <PublicReportLogo theme="dark" />
        <Typography variant="h5" color="error" mb={2}>
          {error}
        </Typography>
        <Typography variant="body1">
          The report could not be loaded. The link may be invalid or expired.
        </Typography>
      </Box>
    );
  }

  if (!isVerified) {
    return (
      <Box className="relative min-h-screen flex flex-col justify-center items-center dark:bg-gradient-to-b from-gray-900 to-gray-800 grad p-4">
        <PublicReportLogo theme={publicTheme} />
        <PublicThemeToggle theme={publicTheme} onToggle={handleToggleTheme} />
        <Box>
          <Typography
            variant="h5"
            className="font-bold mb-4 text-center text-white"
            style={{
              fontFamily: "Poppins",
              color: `${appTheme === "dark" ? "white" : "black"}`,
            }}
          >
            Verify Account
          </Typography>

          <Box className="flex items-center gap-2 w-full mb-6 mt-6">
            <TextField
              value={maskEmail(email)}
              readOnly
              className="flex-1 dark:text-white !text-textSecondary"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.5rem",
                  backgroundColor: `${
                    appTheme === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgb(255,255,255)"
                  }`,
                  color: `${appTheme === "dark" ? "white" : "#525252"}`,
                  transition: "all 0.2s ease",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
                "& .MuiInputBase-input": {
                  color: `${appTheme === "dark" ? "white" : "#525252"}`,
                  fontSize: "0.9rem",
                  padding: "10px 14px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined
                      fontSize="small"
                      sx={{
                        color: `${
                          appTheme === "dark"
                            ? "rgba(255,255,255,0.6)"
                            : "#525252"
                        }`,
                        marginRight: "-12px",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={handleGenerateOtp}
              disabled={otpLoading}
              sx={{
                borderRadius: "0.5rem",
                borderColor: "rgba(255,255,255,0.2)",
                color: `${appTheme === "dark" ? "white" : "#525252"}`,
                padding: "7px 7px",
                minWidth: "100px",
                fontSize: "0.85rem",
                textTransform: "none",
                fontWeight: 500,
                backgroundColor: `${
                  appTheme === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgb(255,255,255)"
                }`,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: `${
                    appTheme === "dark"
                      ? "rgba(255,255,255,0.5)"
                      : "#525252ff"
                  }`,
                  backgroundColor: `${
                    appTheme === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgb(255,255,255)"
                  }`,
                },
                "&.Mui-disabled": {
                  color: `${
                    appTheme === "dark"
                      ? "rgba(255,255,255,0.3)"
                      : "#52525260"
                  }`,
                  borderColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              {otpLoading ? "Sending..." : "Get OTP"}
            </Button>
          </Box>

          <Box className="flex gap-2 mb-5 w-full justify-between">
            {[...Array(6)].map((_, index) => (
              <TextField
                key={index}
                value={otp[index] || ""}
                onChange={(e) => handleOtpInput(e, index, otp, setOtp)}
                onPaste={(e) => handleOtpPaste(e, setOtp)}
                onKeyDown={(e) => handleOtpKeyDown(e, index, otp, setOtp)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    color: `${appTheme === "dark" ? "white" : "#525252"}`,
                    fontSize: "1.2rem",
                    fontWeight: "500",
                  },
                }}
                id={`otp-input-${index}`}
                sx={{
                  width: "48px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0.5rem",
                    backgroundColor: `${
                      appTheme === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgb(255,255,255)"
                    }`,
                    transition: "all 0.2s ease",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                  },
                  "& .MuiInputBase-input": {
                    padding: "10px 0",
                  },
                }}
              />
            ))}
          </Box>

          {otpError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center", mb: 2 }}
            >
              {otpError}
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={handleVerifyOtp}
            disabled={verifyLoading || !otpSent || otp.length !== 6}
            sx={{
              width: "80%",
              margin: "0 auto",
              display: "block",
              borderRadius: "0.5rem",
              marginBottom: "10px",
              backgroundColor: "#3b82f6",
              color: "white",
              padding: "8px 0",
              fontSize: "0.95rem",
              textTransform: "none",
              fontWeight: 500,
              transition: "all 0.2s ease",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.25)",
              "&:hover": {
                backgroundColor: "#2563eb",
                boxShadow: "0 4px 16px rgba(59, 130, 246, 0.35)",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.4)",
              },
            }}
          >
            {verifyLoading ? "Verifying..." : "Verify OTP"}
          </Button>

          <Typography
            variant="body2"
            className="text-gray-400 mt-4 text-center text-sm"
          >
            Didn't receive the code?{" "}
            <span
              className="text-blue-400 cursor-pointer hover:text-blue-300 transition-colors"
              onClick={handleGenerateOtp}
            >
              Resend
            </span>
          </Typography>
        </Box>
      </Box>
    );
  }

  // On the authenticated routes the org/HR portal layout provides the page
  // background around the report; the public route has no such layout, so
  // reproduce it here. `app-background` (the dark texture image) and `grad`
  // (the light theme's solid colour) must be on separate, nested elements —
  // background-image always paints over background-color on the same element,
  // so on one div the texture would show through in light mode too. The inner
  // `grad` div fills the outer one entirely, covering the texture in light mode
  // and staying transparent (texture visible) in dark mode — same split the
  // portal layout itself uses.
  return (
    <div className="app-background min-h-screen">
      <div className="relative grad min-h-screen p-8 sm:p-10 lg:p-14">
        <PublicReportLogo theme={publicTheme} />
        <PublicThemeToggle theme={publicTheme} onToggle={handleToggleTheme} />
        <EvaluationReportOrgAdmin
          publicToken={token}
          publicSession={{
            session_token: sessionToken,
            email: email.toLowerCase(),
          }}
        />
      </div>
    </div>
  );
};

export default PublicReport;
